import { Button, Combobox, Dialog, DialogContent, DialogFooter, DialogHeader, FormCheckbox, Text } from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import useAppStore, { getDefaultAccount, setDefaultAccount } from "../store/appStateZustand";

function ChooseAccountModal({
  to,
  setAccountChoiceModal,
  title,
  accounts,
  nonAdminTo: _nonAdminTo,
  open,
}) {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const [selectedValue, setSelectedValue] = useState("");
  const [setAsDefault, setSetAsDefault] = useState(false);
  const hasHandledSingleAccount = useRef(false);

  // Check if current selection matches the existing default
  const currentDefault = getDefaultAccount();
  const isCurrentlyDefault = selectedValue === currentDefault;

  // Handle single account case - use useEffect to avoid infinite loops
  useEffect(() => {
    // Only run if modal is open, has accounts, and we haven't handled this yet
    if (!open || !accounts || accounts.length !== 1 || hasHandledSingleAccount.current) {
      return;
    }

    const singleAccount = accounts[0];
    hasHandledSingleAccount.current = true;

    // Close modal
    setAccountChoiceModal(false);

    // Switch account and navigate
    (async () => {
      try {
        // Set loading state via Zustand store
        useAppStore.getState().setAccountSwitching(true);

        // Update account state directly via Zustand store
        useAppStore.setState({
          selectedAccount: singleAccount.artistaccountcode,
          selectedCatalogue: singleAccount.cataloguecode,
        });

        toast.success(`Switched to ${singleAccount.accountname}`, {
          description: `Account: ${singleAccount.artistaccountcode}`,
          duration: 3000,
        });

        navigate({ to });
      }
      catch (error) {
        console.error("Account switch failed:", error);
        toast.error("Failed to switch account", {
          description: "Please try again",
          duration: 4000,
        });
      }
      finally {
        // Clear loading state
        useAppStore.getState().setAccountSwitching(false);
      }
    })();
  }, [open, accounts, setAccountChoiceModal, navigate, to]);

  // Reset the ref when modal closes
  useEffect(() => {
    if (!open) {
      hasHandledSingleAccount.current = false;
    }
  }, [open]);

  const handleConfirmSelection = async () => {
    if (!selectedValue)
      return;

    // Find the selected account object
    const selectedArtistAccount = accounts?.find(
      account => account.artistaccountcode === selectedValue,
    );

    if (!selectedArtistAccount)
      return;

    // Close modal
    setAccountChoiceModal(false);

    try {
      // Set loading state via Zustand store
      useAppStore.getState().setAccountSwitching(true);

      // Update account state directly via Zustand store
      useAppStore.setState({
        selectedAccount: selectedArtistAccount.artistaccountcode,
        selectedCatalogue: selectedArtistAccount.cataloguecode,
      });

      // Set as default if checkbox is checked
      if (setAsDefault) {
        setDefaultAccount(selectedArtistAccount.artistaccountcode);
        toast.success(`${selectedArtistAccount.accountname} set as default`, {
          description: "This account will be selected automatically on login",
          duration: 3000,
        });
      }
      else {
        toast.success(`Switched to ${selectedArtistAccount.accountname}`, {
          description: `Account: ${selectedArtistAccount.artistaccountcode}`,
          duration: 3000,
        });
      }

      // Navigate to the page
      navigate({ to });
    }
    catch (error) {
      console.error("Account switch failed:", error);
      toast.error("Failed to switch account", {
        description: "Please try again",
        duration: 4000,
      });
    }
    finally {
      // Clear loading state
      useAppStore.getState().setAccountSwitching(false);
    }
  };

  // Don't render modal if no accounts
  if (!accounts || accounts.length === 0) {
    return null;
  }

  // Don't render modal content if single account (useEffect will handle it)
  if (accounts.length === 1) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setAccountChoiceModal}>
      <DialogContent>
        <div className="space-y-6">
          <DialogHeader>
            <Text as="subtitle">{t("dashboard:select-account", "Select account")}</Text>
          </DialogHeader>

          <div>
            <div>
              <Text as="p">{title}</Text>
            </div>

            <Combobox
              data={accounts
                ?.sort((a, b) => a.accountname.localeCompare(b.accountname))
                .map(account => ({
                  value: account.artistaccountcode,
                  label: account.accountname,
                })) || []}
              value={selectedValue}
              onValueChange={setSelectedValue}
              placeholder={t("dashboard:choose-account-search", "Search accounts...")}
              searchPlaceholder={t("dashboard:choose-account-search", "Search accounts...")}
              emptyText={t("dashboard:no-accounts-found", "No accounts found.")}
              className="w-full"
              searchByLabel
              disablePortal
            />
          </div>

          {/* Default account checkbox - only show when a value is selected */}
          {selectedValue && (
            <div className="flex items-center">
              <FormCheckbox
                checked={setAsDefault}
                onChange={e => setSetAsDefault(e.target.checked)}
                label={isCurrentlyDefault
                  ? t("dashboard:already-default-account", "This is already your default account")
                  : t("dashboard:set-as-default-account", "Set as my default account")}
                disabled={isCurrentlyDefault}
              />
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              label={t("dashboard:cancel", "Cancel")}
              onClick={() => setAccountChoiceModal(false)}
            />
            <Button
              variant="secondary"
              label={t("dashboard:confirm", "Confirm")}
              onClick={handleConfirmSelection}
              disabled={!selectedValue}
            />
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ChooseAccountModal;
